import Highcharts from 'highcharts'

import { calcGradientColor, hexToRgba } from '@aytm/app/stats/lib/render/colors'

// Defined with the series builder it belongs to.
import { OVERLAY_SERIES_ID } from '@aytm/app/stats/page/question-views/bpm/components/quadrant/components/chart/lib/overlaySeries'

import {
  noTextPlaceholder,
  removeLogicFromText
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'

// answer.scores comes from backend as { min, max, length }; expand to sorted array
// of evenly-spaced values used for axis min/max — ported from the legacy quadrant chart view.
export const expandScores = answer => {
  const s = answer?.scores
  if (!s || !s.length || s.length < 2) return []
  const step = (s.max - s.min) / (s.length - 1)
  return Array.from({ length: s.length }, (_, i) => s.min + i * step)
}

export const getChart = wrapperEl => {
  const container = wrapperEl?.querySelector('.highcharts-container')
  if (!container) return null
  return Highcharts.charts.find(v => v != null && v.container.id === container.id) || null
}

export const calcModelData = ({
  questionVariants,
  batches,
  answer,
  quadrantXAxisValue,
  quadrantYAxisValue,
  quadrantRadiusValue,
  quadrantColorValue,
  colorTheme
}) => {
  const xPosition = Number(quadrantXAxisValue)
  const xVariant = batches.find(v => v.position === xPosition)
  const yPosition = Number(quadrantYAxisValue)
  const yVariant = batches.find(v => v.position === yPosition)

  // A one-attribute question leaves the y axis on its unresolvable position-2 default:
  // render an empty title instead of throwing (same guard the legacy chart view carried).
  const result = {
    xAxisTitle: (xVariant && (removeLogicFromText(xVariant.text) || noTextPlaceholder(xVariant))) || '',
    yAxisTitle: (yVariant && (removeLogicFromText(yVariant.text) || noTextPlaceholder(yVariant))) || ''
  }

  if (answer) {
    result.data = {}
    for (const qv of questionVariants) {
      const k = `qv${qv.position}`
      result.data[k] = {}

      let hexColor
      if (quadrantColorValue === 'none') {
        hexColor = colorTheme[(qv.position - 1) % colorTheme.length]
      } else {
        const min = answer.meanStat?.attr?.min?.[`b${quadrantColorValue}`]
        const max = answer.meanStat?.attr?.max?.[`b${quadrantColorValue}`]
        const val = answer.meanList?.[k]?.[`b${quadrantColorValue}`]
        hexColor = calcGradientColor(min, max, val)
      }
      result.data[k].color = hexToRgba(hexColor, 0.8)

      result.data[k].data = [
        [
          answer.meanList?.[k]?.[`b${quadrantXAxisValue}`],
          answer.meanList?.[k]?.[`b${quadrantYAxisValue}`],
          quadrantRadiusValue === 'none' ? null : answer.meanList?.[k]?.[`b${quadrantRadiusValue}`]
        ]
      ]
    }
  }

  return result
}

const isBrandSeries = s => s?.options?.id != null && s.options.id !== OVERLAY_SERIES_ID

// Highcharts strips every property off destroyed points and series (Point/Series#destroy),
// leaving live-but-empty objects — so `s.points[0]?.select()` walks past the optional chain
// and throws inside select() on `point.series.chart`. Anything reached across a redraw
// boundary must be re-resolved and liveness-checked, not just null-checked.
const selectFirstPoint = (chart, id, selected) => {
  const series = chart.series?.find(s => s?.options?.id === id)
  const point = series?.points?.[0]
  if (!point || point.destroyed) return
  point.select(selected, selected)
}

export const getBrandsFromChart = chart => {
  const selected = chart.getSelectedPoints().map(p => p.series.options.id)
  return selected.length === 1 ? selected : chart.series.filter(isBrandSeries).map(s => s.options.id)
}

export const setPoints = (chart, brands) => {
  if (!brands.length) return

  // Ids, not series references — the deselect pass below can invalidate the objects.
  const ids = chart.series.filter(isBrandSeries).map(s => s.options.id)

  for (const id of ids) selectFirstPoint(chart, id, false)
  for (const id of ids) {
    if (brands.includes(id)) selectFirstPoint(chart, id, true)
  }
}

// The heat-matrix overlay is built declaratively now (see lib/overlaySeries), so nothing
// here injects or removes series -- selection only needs the chart repainted.
export const redrawAfterSelection = chart => {
  chart.redraw()
}

export const selectPoints = (chart, point) => {
  const selected = chart.getSelectedPoints().map(p => p.series.options.id)

  for (const s of chart.series) {
    if (s.options.id === OVERLAY_SERIES_ID) continue

    const show = 0 < selected.length && selected.includes(s.options.id)
    s.update({
      enableMouseTracking: show,
      opacity: show ? 1 : 0,
      showInLegend: show,
      states: show
        ? {
            hover: { brightness: 0.05 },
            inactive: { opacity: 1 }
          }
        : {
            hover: { brightness: 0 },
            inactive: { opacity: 0 }
          },
      zIndex: show ? 2 : 0
    })
  }

  redrawAfterSelection(chart)

  point?.onMouseOver?.()
}
